import { create } from "zustand";
import type { LangCode } from "../data/languages";
import type { Product } from "../data/menu";

export type CartItem = { sku: string; qty: number; product: Product };

type State = {
  language: LangCode | null;
  cart: CartItem[];
  customerName: string;
  orderId: string | null;
  paymentStatus: "idle" | "creating" | "at_terminal" | "processed" | "canceled" | "expired";
  setLanguage: (l: LangCode) => void;
  addToCart: (p: Product, qty?: number) => void;
  setQty: (sku: string, qty: number) => void;
  removeFromCart: (sku: string) => void;
  clearCart: () => void;
  setName: (n: string) => void;
  setOrderId: (id: string | null) => void;
  setPaymentStatus: (s: State["paymentStatus"]) => void;
  reset: () => void;
};

export const useStore = create<State>((set) => ({
  language: null,
  cart: [],
  customerName: "",
  orderId: null,
  paymentStatus: "idle",
  setLanguage: (l) => set({ language: l }),
  addToCart: (p, qty = 1) =>
    set((s) => {
      const existing = s.cart.find((c) => c.sku === p.sku);
      if (existing) {
        return { cart: s.cart.map((c) => (c.sku === p.sku ? { ...c, qty: c.qty + qty } : c)) };
      }
      return { cart: [...s.cart, { sku: p.sku, qty, product: p }] };
    }),
  setQty: (sku, qty) =>
    set((s) => ({
      cart: qty <= 0
        ? s.cart.filter((c) => c.sku !== sku)
        : s.cart.map((c) => (c.sku === sku ? { ...c, qty } : c)),
    })),
  removeFromCart: (sku) => set((s) => ({ cart: s.cart.filter((c) => c.sku !== sku) })),
  clearCart: () => set({ cart: [] }),
  setName: (n) => set({ customerName: n }),
  setOrderId: (id) => set({ orderId: id }),
  setPaymentStatus: (s) => set({ paymentStatus: s }),
  reset: () => set({ language: null, cart: [], customerName: "", orderId: null, paymentStatus: "idle" }),
}));

export const cartTotal = (cart: CartItem[]) =>
  cart.reduce((s, i) => s + i.qty * i.product.price, 0);
